// Mount the Livechat package (#216): build its route from what it needs from
// this app — the initialised i18n instance (it registers its `livechat`
// namespace there), the Marketing page its sync links lead to, the signed-in
// employee, the access token the chat socket authenticates with, and three
// shared components (date range selector, employee select, product-group
// select) — and the error page (as the route's errorElement). App.tsx mounts the
// route from HERE, so the package cannot be mounted unregistered. configs/i18n
// is imported (and so initialised) before the registration runs, whatever order
// main.tsx imports things in. The HTTP client goes through core's registry
// (utils/axios/register-core.ts).
import {
    createLivechatModule,
    type LivechatHost,
} from '@vnetwork/datacentral-livechat'
import i18n from '@/configs/i18n'
import ErrorPage from '@/components/ErrorBoundary/ErrorPage'
import useAuthStore from '@/stores/useAuthStore'
import authSdk from '@/utils/sdk'
import { lazy, Suspense } from 'react'
import { paths } from '@/utils/path'

// Lazy: the date range selector only renders on the analytics page and the two
// selects inside the sync drawer. Imported eagerly here they would pull their
// code (and the selects' loaders) into the entry chunk; before the move they
// loaded with the Livechat route chunk (see MODULE-EXTRACTION.md, Business).
const LazyDateRangeSelector = lazy(
    () => import('@/components/DateRangeSelector')
)
const LazyEmployeeSelect = lazy(
    () => import('@/components/Form/Shared/EmployeeSelect')
)
const LazyProductGroupSelect = lazy(
    () => import('@/components/Form/Shared/ProductGroupSelect')
)
const DateRangeSelector: LivechatHost['DateRangeSelector'] = (props) => (
    <Suspense fallback={null}>
        <LazyDateRangeSelector {...props} />
    </Suspense>
)
const EmployeeSelect: LivechatHost['EmployeeSelect'] = (props) => (
    <Suspense fallback={null}>
        <LazyEmployeeSelect {...props} />
    </Suspense>
)
const ProductGroupSelect: LivechatHost['ProductGroupSelect'] = (props) => (
    <Suspense fallback={null}>
        <LazyProductGroupSelect {...props} />
    </Suspense>
)

const useCurrentEmployee = () => ({ employee: useAuthStore().employee })

// The package does not render the app's ErrorPage; the host adds it as the
// route's errorElement, as for Warehouse (#59), Dayoffs (#204) and Survey.
export const LivechatRoute = {
    ...createLivechatModule({
        i18n,
        // Before the move this was `paths.customerMkt` of the app-wide `paths`
        // inside the room info and the sync drawer: it is named here now, so a
        // renamed route fails typecheck in this file.
        links: {
            customerMkt: paths.customerMkt,
        },
        useCurrentEmployee,
        getAccessToken: () => authSdk.getAccessToken(),
        DateRangeSelector,
        EmployeeSelect,
        ProductGroupSelect,
    }),
    errorElement: <ErrorPage status={500} />,
}
